import React, { useState } from "react";
import Card from "../../components/Card";
import Action from "../../components/Action";
import RadioConditional from "../../components/RadioConditional";
import RadioQuestion from "../../components/RadioQuestion";
import TextQuestion from "../../components/TextQuestion";
import ScrollToTop from "../../components/utils/ScrollToTop";
import { YES_NO_OPTIONS } from "../../constants";
import Image from "../../components/Image";
import imageCatAndHousePlant from "../../images/cat-and-houseplant.png";
import { isMoneyValid, normaliseMoney } from "../../shared/utils/moneyUtils";
import { isEmailValid } from "../../shared/utils/validation";

const defaultErrorMessages = {
  estimateSellingAnotherPropertyAmount: "Please fill this in",
  linkedSaleComplete: "Please choose an option",
  linkedSaleWithJuno: "Please choose an option",
  linkedSaleLawyerHandling: "Please fill this in",
  linkedSaleLawFirm: "Please fill this in",
  linkedSaleLawFirmEmailAddress: "Please enter an email address",
};

const LinkedSale = ({ linkedSale, onStepComplete }) => {
  const [formState, setFormState] = useState({
    estimateSellingAnotherPropertyAmount: linkedSale?.estimateSellingAnotherPropertyAmount || "",
    linkedSaleComplete: linkedSale?.linkedSaleComplete ?? null,
    linkedSaleWithJuno: linkedSale?.linkedSaleWithJuno ?? null,
    linkedSaleLawyerHandling: linkedSale?.linkedSaleLawyerHandling || "",
    linkedSaleLawFirm: linkedSale?.linkedSaleLawFirm || "",
    linkedSaleLawFirmEmailAddress: linkedSale?.linkedSaleLawFirmEmailAddress || "",
    linkedSaleLawFirmPhoneNumber: linkedSale?.linkedSaleLawFirmPhoneNumber || "",
  });
  const [errors, setErrors] = useState({});
  const [errorMessages, setErrorMessages] = useState({ ...defaultErrorMessages });

  const handleInputChange = (field) => (value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const selectLinkedSaleComplete = (value) => {
    setFormState((prev) => ({
      ...prev,
      linkedSaleComplete: value,
      // Reset linkedSaleWithJuno when linkedSaleComplete changes to false
      linkedSaleWithJuno: value === false ? null : prev.linkedSaleWithJuno,
    }));
  };

  const handleValidation = () => {
    let newErrors = {};
    let newErrorMessages = { ...errorMessages };
    let formIsValid = true;

    if (!formState.estimateSellingAnotherPropertyAmount) {
      newErrors.estimateSellingAnotherPropertyAmount = true;
      formIsValid = false;
    }

    if (
      formState.estimateSellingAnotherPropertyAmount !== "" &&
      !isMoneyValid(formState.estimateSellingAnotherPropertyAmount)
    ) {
      newErrors.estimateSellingAnotherPropertyAmount = true;
      newErrorMessages.estimateSellingAnotherPropertyAmount = "Please enter a valid amount";
      formIsValid = false;
    }

    if (formState.linkedSaleComplete === null) {
      newErrors.linkedSaleComplete = true;
      formIsValid = false;
    }

    if (formState.linkedSaleComplete === false) {
      if (formState.linkedSaleWithJuno === null) {
        newErrors.linkedSaleWithJuno = true;
        formIsValid = false;
      }
    }

    if (formState.linkedSaleWithJuno === false) {
      if (!formState.linkedSaleLawyerHandling) {
        newErrors.linkedSaleLawyerHandling = true;
        formIsValid = false;
      }

      if (!formState.linkedSaleLawFirm) {
        newErrors.linkedSaleLawFirm = true;
        formIsValid = false;
      }

      if (!formState.linkedSaleLawFirmEmailAddress) {
        newErrors.linkedSaleLawFirmEmailAddress = true;
        newErrorMessages.linkedSaleLawFirmEmailAddress = "Please enter an email address";
        formIsValid = false;
      }

      if (formState.linkedSaleLawFirmEmailAddress !== "" && !isEmailValid(formState.linkedSaleLawFirmEmailAddress)) {
        newErrors.linkedSaleLawFirmEmailAddress = true;
        newErrorMessages.linkedSaleLawFirmEmailAddress = "Please enter a valid email address";
        formIsValid = false;
      }
    }

    setErrors(newErrors);
    setErrorMessages(newErrorMessages);
    return formIsValid;
  };

  const handleFieldValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    setErrors((prev) => {
      let newErrors = { ...prev };

      if (!isBlur) {
        newErrors[field] = false;
      } else {
        if (!formState[field]) {
          newErrors[field] = true;
        }
      }

      return newErrors;
    });
  };

  const handleEmailAddressValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    setErrors((prev) => {
      let newErrors = { ...prev };

      if (!isBlur) {
        newErrors[field] = false;
      } else {
        if (!formState[field]) {
          newErrors[field] = true;
        } else if (!isEmailValid(formState[field])) {
          newErrors[field] = true;
        }
      }

      return newErrors;
    });
    setErrorMessages((prev) => {
      let newErrorMessages = { ...prev };

      if (isBlur) {
        if (!formState[field]) {
          newErrorMessages[field] = "Please enter an email address";
        } else if (!isEmailValid(formState[field])) {
          newErrorMessages[field] = "Please enter a valid email address";
        }
      }

      return newErrorMessages;
    });
  };

  const handleMoneyValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    setErrors((prev) => {
      let newErrors = { ...prev };

      if (!isBlur) {
        newErrors[field] = false;
      } else {
        if (!formState[field]) {
          newErrors[field] = true;
        } else if (!isMoneyValid(formState[field])) {
          newErrors[field] = true;
        }
      }

      return newErrors;
    });
    setErrorMessages((prev) => {
      let newErrorMessages = { ...prev };

      if (isBlur) {
        if (!formState[field]) {
          newErrorMessages[field] = "Please fill this in";
        } else if (!isMoneyValid(formState[field])) {
          newErrorMessages[field] = "Please enter a valid amount";
        }
      }

      return newErrorMessages;
    });
  };

  const saveLinkedSale = (event) => {
    event.preventDefault();

    if (handleValidation()) {
      onStepComplete({
        linkedSale: {
          estimateSellingAnotherPropertyAmount: normaliseMoney(formState.estimateSellingAnotherPropertyAmount),
          linkedSaleComplete: formState.linkedSaleComplete,
          linkedSaleWithJuno: formState.linkedSaleWithJuno,
          linkedSaleLawyerHandling: formState.linkedSaleLawyerHandling,
          linkedSaleLawFirm: formState.linkedSaleLawFirm,
          linkedSaleLawFirmEmailAddress: formState.linkedSaleLawFirmEmailAddress,
          linkedSaleLawFirmPhoneNumber: formState.linkedSaleLawFirmPhoneNumber,
        },
      });
    }
  };

  return (
    <React.Fragment>
      <ScrollToTop />
      <Card title="Tell us about your sale" removeTopPadding>
        <TextQuestion
          question="How much money from your sale will you contribute to your purchase?"
          hint="This amount is usually lower than the sale price"
          value={formState.estimateSellingAnotherPropertyAmount}
          valueEntered={handleInputChange("estimateSellingAnotherPropertyAmount")}
          validate={handleMoneyValidation("estimateSellingAnotherPropertyAmount")}
          validationError={
            errors.estimateSellingAnotherPropertyAmount ? errorMessages.estimateSellingAnotherPropertyAmount : null
          }
        />

        <RadioQuestion
          question="Has your sale already completed?"
          options={YES_NO_OPTIONS}
          inline={true}
          name="linkedSaleComplete"
          selected={formState.linkedSaleComplete}
          optionSelected={selectLinkedSaleComplete}
          validate={handleFieldValidation("linkedSaleComplete")}
          validationError={errors.linkedSaleComplete ? errorMessages.linkedSaleComplete : null}
        />

        {formState.linkedSaleComplete === false && (
          <RadioConditional>
            <RadioQuestion
              question="Is Juno handling your sale?"
              options={YES_NO_OPTIONS}
              inline={true}
              name="linkedSaleWithJuno"
              selected={formState.linkedSaleWithJuno}
              optionSelected={handleInputChange("linkedSaleWithJuno")}
              validate={handleFieldValidation("linkedSaleWithJuno")}
              validationError={errors.linkedSaleWithJuno ? errorMessages.linkedSaleWithJuno : null}
            />

            {formState.linkedSaleWithJuno === false && (
              <React.Fragment>
                <TextQuestion
                  question="What’s the name of the lawyer handling your sale?"
                  value={formState.linkedSaleLawyerHandling}
                  valueEntered={handleInputChange("linkedSaleLawyerHandling")}
                  validate={handleFieldValidation("linkedSaleLawyerHandling")}
                  validationError={errors.linkedSaleLawyerHandling ? errorMessages.linkedSaleLawyerHandling : null}
                />

                <TextQuestion
                  question="What’s the name of the law firm they work for?"
                  value={formState.linkedSaleLawFirm}
                  valueEntered={handleInputChange("linkedSaleLawFirm")}
                  validate={handleFieldValidation("linkedSaleLawFirm")}
                  validationError={errors.linkedSaleLawFirm ? errorMessages.linkedSaleLawFirm : null}
                />

                <TextQuestion
                  question="What’s their email address?"
                  value={formState.linkedSaleLawFirmEmailAddress}
                  valueEntered={handleInputChange("linkedSaleLawFirmEmailAddress")}
                  validate={handleEmailAddressValidation("linkedSaleLawFirmEmailAddress")}
                  validationError={
                    errors.linkedSaleLawFirmEmailAddress ? errorMessages.linkedSaleLawFirmEmailAddress : null
                  }
                />

                <TextQuestion
                  question="What’s their phone number?"
                  value={formState.linkedSaleLawFirmPhoneNumber}
                  valueEntered={handleInputChange("linkedSaleLawFirmPhoneNumber")}
                />
              </React.Fragment>
            )}
          </RadioConditional>
        )}

        <Action onClick={saveLinkedSale}>Continue</Action>
      </Card>
      <Image src={imageCatAndHousePlant} />
    </React.Fragment>
  );
};

export default LinkedSale;
